import { useState } from 'react';
import {
  addMonths,
  isAfter,
  isBefore,
  isSameDay,
  isWithinInterval,
} from 'date-fns';

import type { DateRange, Marker, NavigationAction } from './types';
import {
  AVAILABLE_MAX_DATE,
  AVAILABLE_MIN_DATE,
  getValidatedMonths,
  MARKERS,
  parseOptionalDate,
} from './utils';

type UseDateRangePickerProps = {
  initialDateRange?: DateRange;
  minDate?: Date | string;
  maxDate?: Date | string;
  onChange?: (dateRange: DateRange) => void;
};

export const useDateRangePicker = (props: UseDateRangePickerProps) => {
  const today = new Date();
  const {
    onChange: onChangeCallback,
    initialDateRange,
    minDate,
    maxDate,
  } = props;

  const minValidDate = parseOptionalDate(minDate, AVAILABLE_MIN_DATE);
  const maxValidDate = parseOptionalDate(maxDate, AVAILABLE_MAX_DATE);

  const [initialFirstMonth, initialSecondMonth] = getValidatedMonths(
    initialDateRange ?? {},
    minValidDate,
    maxValidDate,
  );
  const [dateRange, setDateRange] = useState<DateRange>({
    ...initialDateRange,
  });
  const [hoverDay, setHoverDay] = useState<Date>();
  const [firstMonth, setFirstMonth] = useState<Date>(
    initialFirstMonth ?? today,
  );
  const [secondMonth, setSecondMonth] = useState<Date>(
    initialSecondMonth ?? addMonths(firstMonth, 1),
  );

  const { startDate, endDate } = dateRange;

  const handleSetFirstMonth = (date: Date) => {
    if (isBefore(date, secondMonth)) {
      if (isAfter(date, minValidDate)) {
        setFirstMonth(date);
      } else {
        setFirstMonth(minValidDate);
      }
      return;
    }
    if (isBefore(addMonths(date, 1), maxValidDate)) {
      setFirstMonth(date);
      setSecondMonth(addMonths(date, 1));
    } else {
      setSecondMonth(maxValidDate);
      setFirstMonth(addMonths(maxValidDate, -1));
    }
  };

  const handleSetSecondMonth = (date: Date) => {
    if (isAfter(date, firstMonth)) {
      if (isBefore(date, maxValidDate)) {
        setSecondMonth(date);
      } else {
        setSecondMonth(maxValidDate);
      }
      return;
    }
    if (isAfter(addMonths(date, -1), minValidDate)) {
      setSecondMonth(date);
      setFirstMonth(addMonths(date, -1));
    } else {
      setFirstMonth(minValidDate);
      setSecondMonth(addMonths(minValidDate, 1));
    }
  };

  const handleClickDateNumber = (day: Date) => {
    if (startDate && !endDate && !isBefore(day, startDate)) {
      const newRange = { startDate, endDate: day };
      onChangeCallback?.(newRange);
      setDateRange(newRange);
    } else {
      setDateRange({ startDate: day, endDate: undefined });
    }
    setHoverDay(day);
  };

  const handleClickNavIcon = (marker: Marker, action: NavigationAction) => {
    if (marker === MARKERS.FIRST_MONTH) {
      const firstNew = addMonths(firstMonth, action);
      if (isBefore(firstNew, secondMonth)) setFirstMonth(firstNew);
    } else {
      const secondNew = addMonths(secondMonth, action);
      if (isBefore(firstMonth, secondNew)) setSecondMonth(secondNew);
    }
  };

  const handleHoverDateNumber = (date: Date) => {
    if (startDate && !endDate) {
      if (!hoverDay || !isSameDay(date, hoverDay)) {
        setHoverDay(date);
      }
    }
  };

  const isInHoverRange = (day: Date): boolean =>
    Boolean(
      startDate &&
        !endDate &&
        hoverDay &&
        isAfter(hoverDay, startDate) &&
        isWithinInterval(day, { start: startDate, end: hoverDay }),
    );

  return {
    dateRange,
    minDate: minValidDate,
    maxDate: maxValidDate,
    firstMonth,
    secondMonth,
    handleSetFirstMonth,
    handleSetSecondMonth,
    helpers: { isInHoverRange },
    handlers: {
      handleClickDateNumber,
      handleClickNavIcon,
      handleHoverDateNumber,
    },
  };
};
